import React, { useState, useEffect } from "react";
import Button, { ButtonStyles } from "../common/Button";
import InfoModal from "../common/InfoModal";
import { PromptModal } from "../common/PromptModal";
import TelematicsApi from "../../api/telematicsApi";
import {
  AllDriveEasyProgramEligibility,
  SetDriveEasyProgramStatusRequest,
} from "../../models/AllDriveEasyProgram";
import { useDispatch } from "react-redux";
import { IS_LOADING, FINISHED_LOADING } from "../../store/Actions";

const headers = ["State", "Channel", "Program", "Involuntary/Voluntary"];
const voluntaryStatusToId = { Voluntary: 1, Involuntary: 2 };

export const InvoluntaryTelematics = () => {
  const [
    allDriveEasyProgramEligibilityData,
    setAllDriveEasyProgramEligibilityData,
  ] = useState<AllDriveEasyProgramEligibility[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [changesSubmitted, setChangesSubmitted] = useState(false);
  const [showSubmitPromptModal, setShowSubmitPromptModal] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [formMessageColor, setFormMessageColor] = useState("");
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchAllDriveEasyProgramEligibilityData();
  }, []);

  const fetchAllDriveEasyProgramEligibilityData = async () => {
    try {
      dispatch({ type: IS_LOADING });
      const response = await TelematicsApi.getAllDriveEasyProgramEligibility();
      setAllDriveEasyProgramEligibilityData(response);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
    } finally {
      dispatch({ type: FINISHED_LOADING });
    }
  };

  const reset = () => {
    fetchAllDriveEasyProgramEligibilityData();
    setIsDirty(false);
    setChangesSubmitted(false);
    setFormMessageColor("red");
    setFormMessage("Changes have been removed and not applied.");
  };

  const onChange = (id: number, voluntaryStatusValue: string) => {
    const updatedData = allDriveEasyProgramEligibilityData.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          isEnabled: voluntaryStatusValue !== "Off",
          voluntaryStatus:
            voluntaryStatusValue !== "Off"
              ? voluntaryStatusValue
              : item.voluntaryStatus,
          voluntaryStatusId:
            voluntaryStatusValue !== "Off"
              ? voluntaryStatusToId[
                  voluntaryStatusValue as keyof typeof voluntaryStatusToId
                ]
              : item.voluntaryStatusId,
          isDirty: true,
        };
      }
      return item;
    });

    setAllDriveEasyProgramEligibilityData(updatedData);
    setIsDirty(true);
    setChangesSubmitted(false);
  };

  const mapToSetDriveEasyProgramStatusRequest = (
    dirtyData: AllDriveEasyProgramEligibility[]
  ): SetDriveEasyProgramStatusRequest => {
    return {
      stateId: dirtyData.map((item) => item.stateId),
      channelId: dirtyData.map((item) => item.channelId),
      programId: dirtyData.map((item) => item.programId),
      volStatus: dirtyData.map((item) => item.voluntaryStatusId),
      isEnabled: dirtyData.map((item) => (item.isEnabled ? 1 : 0)),
    };
  };

  const submitChanges = async () => {
    try {
      const dirtyData = allDriveEasyProgramEligibilityData.filter(
        (item) => item.isDirty
      );
      const formattedDirtyData =
        mapToSetDriveEasyProgramStatusRequest(dirtyData);
      dispatch({ type: IS_LOADING });
      const response = await TelematicsApi.updateAllDriveEasyProgramStatus(
        formattedDirtyData
      );
      if (response.ok) {
        setFormMessageColor("green");
        setFormMessage("Changes have been committed and saved.");
        setChangesSubmitted(true);
        fetchAllDriveEasyProgramEligibilityData();
      } else {
        throw new Error("Failed to save changes.");
      }
    } catch (err) {
      setShowSubmitPromptModal(false);
      setError("Failed to save changes. Please try again.");
      setFormMessageColor("red");
      setFormMessage(
        "Changes have not been committed due to a technical error."
      );
    } finally {
      setShowSubmitPromptModal(false);
      dispatch({ type: FINISHED_LOADING });
    }
  };

  return (
    <div className="container margin-top">
      {error && (
        <InfoModal
          title="Technical Error"
          infoText={error}
          onClose={() => setError(null)}
        />
      )}
      {showSubmitPromptModal && (
        <PromptModal
          title="Update Policy Information"
          infoText="You are about to submit these changes. Are you sure you wish to continue?"
          yes={submitChanges}
          no={() => {
            setShowSubmitPromptModal(false);
            setFormMessageColor("red");
            setFormMessage("Changes have not been saved.");
          }}
        />
      )}
      <table className="table table--non-sortable table--editable-non-sortable">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} style={{ textAlign: "left" }}>
                {header}
              </th>
            ))}
            <th>
              <Button
                title="Reset"
                onClick={reset}
                btnStyle={ButtonStyles.BlueCenteredTableButton}
              />
            </th>
            <th>
              <Button
                title="Submit"
                disabled={!(isDirty && !changesSubmitted)}
                onClick={() => setShowSubmitPromptModal(true)}
                btnStyle={ButtonStyles.BlueCenteredTableButton}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {allDriveEasyProgramEligibilityData.map((item) => (
            <tr
              key={item.id}
              style={{ fontWeight: item.isDirty ? "bold" : "normal" }}
            >
              <td>{item.stateCode}</td>
              <td>{item.channel}</td>
              <td>{item.programCode}</td>
              <td>
                <select
                  style={{ width: "94px" }}
                  value={item.isEnabled ? item.voluntaryStatus : "Off"}
                  onChange={(e) => onChange(item.id, e.target.value)}
                >
                  <option value="Involuntary">Involuntary</option>
                  <option value="Voluntary">Voluntary</option>
                  <option value="Off">Off</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginRight: "20px",
          marginBottom: "20px",
        }}
      >
        <Button
          title="Reset"
          onClick={reset}
          btnStyle={ButtonStyles.BlueCenteredTableButton}
        />
        <Button
          title="Submit"
          disabled={!(isDirty && !changesSubmitted)}
          onClick={() => setShowSubmitPromptModal(true)}
          btnStyle={ButtonStyles.BlueCenteredTableButton}
        />
      </div>
      {(isDirty || changesSubmitted) && (
        <p className={formMessageColor}>{formMessage}</p>
      )}
    </div>
  );
};

export default InvoluntaryTelematics;
